/**
 * Who owns a database that the system manages: an App Container that shows
 * it, the system named in `managedBy`, or its source doc. Shared by the
 * databases list and the trash tab.
 */
import { useGuard } from '@affine/core/components/guard';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { WorkbenchService } from '../../../workbench/services/workbench';
import { WorkspaceService } from '../../../workspace';
import { useWorkspaceAppBases } from '../../hooks/use-workspace-app-bases';
import type { DatabaseInfo } from '../../services/database-discovery';
import {
  describeManagedDatabaseOwner,
  resolveAppBaseDatabaseOwner,
} from './managed-database-owner';

type DatabaseRef = Pick<DatabaseInfo, 'blockId' | 'docId'> & {
  managedBy?: string;
};

export function useDatabaseOwners() {
  const workspace = useService(WorkspaceService).workspace;
  const workbenchService = useService(WorkbenchService);
  const canViewAppBases = useGuard('Workspace_AppBases_View') === true;
  const { views: appBases } = useWorkspaceAppBases(
    canViewAppBases ? workspace.id : null
  );

  const resolveAppBaseOwner = useCallback(
    (database: DatabaseRef) =>
      resolveAppBaseDatabaseOwner({
        blockId: database.blockId,
        docId: database.docId,
        appBases,
        openAppBase: appBaseId =>
          workbenchService.workbench.open(`/app-bases/${appBaseId}`, {
            at: 'active',
          }),
      }),
    [appBases, workbenchService]
  );

  const resolveManagedOwner = useCallback(
    (database: DatabaseRef, docTitle: string) =>
      describeManagedDatabaseOwner({
        managedBy: database.managedBy,
        fallbackDocTitle: docTitle,
        explicitOwner: resolveAppBaseOwner(database),
        openSourceDoc: () =>
          workbenchService.workbench.open(`/${database.docId}`),
      }),
    [resolveAppBaseOwner, workbenchService]
  );

  return { resolveAppBaseOwner, resolveManagedOwner };
}
